import {formatDistanceToNow} from 'date-fns';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from '@kroll-resolver-crisp/ui-components-react';
import {type SubmissionHistory, type SubmissionHistoryEvent} from '@/api/submissions';
import {cn} from '@/lib/utils';
import type {StatusTone} from '@/lib/status';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {
  customerDecisionLabel,
  customerFeedbackDetails,
  isFalsePositiveDecision
} from '@/lib/customerFeedback';

const HISTORY_EVENT_LABELS: Record<string, string> = {
  SubmissionCreated: 'Submitted',
  SubmissionQueued: 'Queued in hopper',
  SubmissionEscalated: 'Escalated for delivery',
  SubmissionQcReviewRequired: 'Routed to QC review',
  SubmissionHeldForP0: 'Held until the P0 alert is approved',
  SubmissionQcApprovedQueued: 'QC approved — queued in hopper',
  SubmissionQcApprovedEscalated: 'QC approved — escalated for delivery',
  SubmissionQcRejected: 'Rejected by QC review',
  SubmissionDeliveryRequested: 'Delivery requested',
  SubmissionDeliverySucceeded: 'Delivered to client',
  SubmissionDeliveryFailedTerminal: 'Delivery failed (no more retries)',
  SubmissionDeliverySkipped: 'Not sent - content already removed',
  SubmissionRetryRequested: 'Retry requested',
  SubmissionRemoved: 'Removed',
  SubmissionStatusChecked: 'Status checked',
  SubmissionPlatformFeedbackCaptured: 'Platform feedback captured',
  SubmissionPlatformAttributesUpdated: 'Metadata updated',
  SubmissionPlatformAttributesInitiallyObserved: 'Baseline data captured',
  SubmissionPlatformAttributesNoise: 'Metadata updated',
  SubmissionApolloSendRequested: 'Queued for Apollo',
  SubmissionCalibrated: 'Calibrated',
  SubmissionCalibrationNoted: 'Calibration note',
  // Synthesized server-side from the apollo outbox row's sent_at - Apollo sends record no lifecycle
  // event of their own. See SubmissionsController.GetHistory.
  SentToApollo: 'Sent to Apollo',
  // Synthesized from the item's own event log - see SubmissionsController.GetHistory.
  ThumbnailChanged: 'Thumbnail changed'
};

function historyEventLabel(event: SubmissionHistoryEvent): string {
  if (event.type === 'DeliveryAttempt') {
    return event.detail ?? 'Delivery attempt';
  }

  return HISTORY_EVENT_LABELS[event.type] ?? event.type;
}

type TimelineEntry = {
  key: string;
  label: string;
  occurredAt: string;
  detail: string | null;
  // Shown inline as "By ...". Null for the milestones derived from the item/job rather than from
  // an event, which have no single actor to name.
  actor: string | null;
  // Painted by the shared StatusIcon beside the label; the label says what happened, so the colour
  // is emphasis rather than the only signal. An event that is neither an outcome nor a routine
  // check is "info": it happened, and it is not good or bad news.
  tone: StatusTone;
};

function advancedEntryTone(event: SubmissionHistoryEvent): StatusTone {
  if (event.type === 'SubmissionRemoved' || event.type === 'SubmissionDeliveryFailedTerminal') {
    return 'destructive';
  }

  if (event.type === 'SubmissionDeliverySucceeded') {
    return 'success';
  }

  if (
    event.type === 'SubmissionStatusChecked' ||
    event.type === 'SubmissionPlatformFeedbackCaptured'
  ) {
    return 'muted';
  }

  return 'info';
}

// The full raw event log, for debugging - every recorded event, unfiltered.
export function buildAdvancedTimelineEntries(events: SubmissionHistoryEvent[]): TimelineEntry[] {
  return events.map((event) => ({
    key: `${event.type}-${event.occurred_at}-${event.detail ?? ''}`,
    label: historyEventLabel(event),
    actor: event.actor_email,
    occurredAt: event.occurred_at,
    detail: event.detail && event.type !== 'DeliveryAttempt' ? event.detail : null,
    tone: advancedEntryTone(event)
  }));
}

// The 5 milestones an analyst actually cares about at a glance - published, collected, classified,
// delivered, and actioned (removed or flagged by the platform) - rather than the full event log.
export function buildBasicTimelineEntries(history: SubmissionHistory): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  if (history.item?.published_at) {
    entries.push({
      key: 'published',
      label: 'Published',
      occurredAt: history.item.published_at,
      detail: null,
      actor: null,
      tone: 'info'
    });
  }

  if (history.item) {
    entries.push({
      key: 'collected',
      label: 'Collected',
      occurredAt: history.item.collected_at,
      detail: history.job ? (history.job.search_term ?? history.job.source) : 'Manually submitted',
      actor: history.job?.created_by ?? null,
      tone: 'info'
    });
  }

  if (history.classification) {
    entries.push({
      key: 'classified',
      label: 'Classified',
      occurredAt: history.classification.created_at,
      detail: `${history.classification.policy_violation} / ${history.classification.risks.join(', ')}`,
      actor: history.classification.analyst_email,
      tone: 'info'
    });
  }

  for (const event of history.timeline) {
    switch (event.type) {
      case 'SubmissionDeliverySucceeded': {
        entries.push({
          key: `delivered-${event.occurred_at}`,
          label: 'Delivered',
          occurredAt: event.occurred_at,
          detail: event.detail,
          actor: event.actor_email,
          tone: 'success'
        });
        break;
      }

      case 'SubmissionCalibrated':
      case 'SubmissionCalibrationNoted': {
        entries.push({
          key: `calibrated-${event.occurred_at}`,
          label: historyEventLabel(event),
          occurredAt: event.occurred_at,
          detail: event.detail,
          actor: event.actor_email,
          tone: 'info'
        });
        break;
      }

      case 'SubmissionRemoved':
      case 'SubmissionPlatformAttributesUpdated': {
        entries.push({
          key: `actioned-${event.occurred_at}`,
          label: 'Actioned',
          occurredAt: event.occurred_at,
          detail: event.detail,
          actor: event.actor_email,
          tone: event.type === 'SubmissionRemoved' ? 'destructive' : 'info'
        });
        break;
      }

      default: {
        break;
      }
    }
  }

  // The customer's own outcome, dated when their feedback file was uploaded - the last step of the
  // lifecycle, after delivery and whatever the platform did. Flag day is when we flagged it to them.
  const feedback = history.customer_feedback;
  if (feedback) {
    entries.push({
      key: 'customer-feedback',
      label: `Customer feedback: ${customerDecisionLabel(feedback.last_decision) ?? 'no decision'}`,
      occurredAt: feedback.uploaded_at,
      detail: [
        ...customerFeedbackDetails(feedback).map(({label, value}) => `${label}: ${value}`),
        `Flagged to customer ${feedback.flag_day}`
      ].join('. '),
      actor: feedback.uploaded_by,
      // Approve / No violation is the customer calling it a false positive.
      tone: isFalsePositiveDecision(feedback.last_decision) ? 'warning' : 'info'
    });
  }

  // Platform-detected removal - the only "Actioned" signal SubmissionPlatformAttributesUpdated
  // above can never carry for Falcon, since pinterest_profile's status check has no boolean
  // attribute fields to diff. Keyed off the read model's own latest status rather than a specific
  // event type, so it fires the same way for either platform once the item is actually gone.
  // A fallback, not an addition: on YouTube a removal usually already produced one of the events
  // above, and two "Actioned" milestones for the same removal read as two separate actions.
  const alreadyActioned = entries.some((entry) => entry.label === 'Actioned');
  if (
    !alreadyActioned &&
    history.platform_status.status === 'removed' &&
    history.platform_status.status_changed_at
  ) {
    entries.push({
      key: 'actioned-platform-removed',
      label: 'Actioned',
      occurredAt: history.platform_status.status_changed_at,
      detail: 'Platform reports this as removed',
      // Derived from the read model's own status, not from an event - there is no actor to name.
      actor: null,
      tone: 'destructive'
    });
  }

  return entries.sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  );
}

// Vertical, not the horizontal dot-track the table-row version used: a side drawer is narrow, and
// a vertical list also puts each entry's By/date/comment straight on the page instead of behind a
// hover - which matters most for a Calibrated entry, the one place a comment is the whole point.
export function SubmissionTimeline({entries}: {readonly entries: TimelineEntry[]}) {
  if (entries.length === 0) {
    return (
      <Empty variant="outline" className="gap-2 p-4">
        <EmptyHeader className="gap-0.5">
          <EmptyTitle>No events yet</EmptyTitle>
          <EmptyDescription>
            Nothing has happened to this submission since it was created.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <ol>
      {entries.map((entry, index) => {
        const isDestructive = entry.tone === 'destructive';
        const occurredAt = new Date(entry.occurredAt);
        const isLast = index === entries.length - 1;

        return (
          <li key={entry.key} className="relative flex gap-3 pb-4">
            {isLast ? null : (
              <span className="bg-border absolute top-[1.125rem] left-[6.5px] h-full w-px" />
            )}
            <StatusIcon tone={entry.tone} className="relative z-10 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span
                  className={cn('text-xs font-medium', isDestructive && 'text-destructive-ink')}>
                  {entry.label}
                </span>
                <span className="text-muted-foreground text-xs">
                  {occurredAt.toLocaleDateString()} ·{' '}
                  {formatDistanceToNow(occurredAt, {addSuffix: true})}
                </span>
              </div>
              {entry.actor ? (
                <p className="text-muted-foreground mt-0.5 text-xs">By {entry.actor}</p>
              ) : null}
              {entry.detail ? (
                <p className="text-foreground mt-0.5 text-xs whitespace-pre-wrap">{entry.detail}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
